import {SecureStoragePlugin} from "capacitor-secure-storage-plugin";
import {offlineStorageGet, offlineStorageRemove, offlineStorageSet} from "./offlineSync.service";
import {LogisticContainer, Order, Report} from "../app/reportPage/reportPage.component";
import {Preferences} from "@capacitor/preferences";
import moment from "moment/moment";
import {ReportStatus} from "../enums/reportStatus";
import {Report as ReportApi, ReportWorker} from "../models/raport";
import {DataSelectable} from "../app/reportPage/components/report/reportRowForm.component";
import {v4 as uuidv4} from 'uuid';
import {Contract as ContractModel} from "@models/contract";
import {isAvailabilityPreferredPlacesEnabled} from "@services/calendar/availability.service";
import {getServerNow} from "./serverDate.service";

export interface OfflineDataOrder {
    componentNumbers: DataSelectable[];
    defects: {
        specification_defects: DataSelectable[],
        general_defects: DataSelectable[],
    };
    order: Order;
    measuringCategories: OfflineDataReportMeasurement[] | null,
    errors: string[];
    containers: LogisticContainer[] | null;
}

export interface WorkerAvailabilityRange {
    startTime: string;
    endTime: string;
    preferredPlaceIds: number[];
    agreementForOuterPlaces: boolean;
}

export interface WorkerAvailabilities {
    [workerId: string]: {
        [date: string]: WorkerAvailabilityRange[];
    };
}

export interface OfflineDataReport {
    orders: OfflineDataOrder[];
    workers: ReportWorker[];
    activeContract: ContractModel
    generalDefects: DataSelectable[];
    internalCosts: DataSelectable[];
    availabilities?: WorkerAvailabilities;
}

export type OfflineDataReportMeasurement = {
    measuring_equipment_item_category: DataSelectable;
    items: DataSelectable[] | null;
    multiplier?: number;
    missing?: boolean;
    hide?: boolean;
}

export const OFFLINE_REPORT_ID_PREFIX = 'report_';

const findSpecificationByNumber = async (number: bigint | string) => {
    let specId:any = null;
    let keys = await SecureStoragePlugin.keys();
    let workerNumber = await Preferences.get({'key': 'employee_number'});

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (specId === null &&
                        order.order.specification != null &&
                        order.order.specification.id &&
                        order.order.specification.id == parseInt(number.toString()) &&
                        workerExistsInReport(posData, workerNumber.value ? workerNumber.value : '')) {
                        specId = number;
                    }
                })
            }
        }
    }

    return specId;
}

const checkSpecification = async (id: bigint | string, reportId = '', skipDetailedCheck:boolean = false) => {
    let orderData:any = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (orderData === null && order.order.specification != null && order.order.specification.id && order.order.specification.id == parseInt(id.toString())) {
                        if (order.errors.length > 0 && !skipDetailedCheck) {
                            orderData = order.errors.shift();
                        } else {
                            orderData = order.order;
                        }
                    }
                })
            }
        }
    }

    return orderData;
}

const findOrderById = async (number: bigint | string) => {
    let orderData: Order | null = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (orderData === null && order.order.id == number) {
                        orderData = order.order;
                    }
                })
            }
        }
    }

    return orderData;
}

const findOrderByNumber =  async (number: bigint | string, skipDetailedCheck:boolean = false) => {
    let orderData:any = null;
    let keys = await SecureStoragePlugin.keys();
    let workerNumber = await Preferences.get({'key': 'employee_number'});

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (order.order.sub_type === 'OPTIMIZATION' || order.order.sub_type === 'CONTROL') {
                        return 'ORDER_TYPE_NOT_ALLOWED';
                    }
                    if (orderData === null && order.order.specification == null &&
                        order.order.formatted_number == number &&
                        workerExistsInReport(posData, workerNumber.value ? workerNumber.value : '')) {
                        if (order.errors.length > 0 && !skipDetailedCheck) {
                            orderData = order.errors.shift();
                        } else {
                            orderData = order.order;
                        }
                    }
                })
            }
        }
    }

    return orderData ? orderData : 'COMMON';
}

const checkWorker = async (orderId: number|string, number: string, serviceDate: string) => {
    let workerData: ReportWorker | null = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (workerData === null && order.order.id == orderId) {
                        posData.workers.map((worker) => {
                            if (worker.number == number) {
                                workerData = worker;
                            }
                        })
                    }
                })
            }
        }
    }

    return workerData;
}

const getReport = async (reportId: number|string) => {
    return await offlineStorageGet(OFFLINE_REPORT_ID_PREFIX + reportId.toString());
}

const orderComponentsNumbers = async (orderId: number|string) => {
    let components: DataSelectable[] | null = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (components === null && order.order.id == orderId) {
                        components = order.componentNumbers;
                    }
                })
            }
        }
    }

    return components;
}

const orderContainers = async (orderId: number|string) => {
    let containers: LogisticContainer[] | null = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (containers === null && order.order.id == orderId) {
                        containers = order.containers;
                    }
                })
            }
        }
    }

    return containers;
}

const findContainerById = async (id: bigint | string) => {
    let container: LogisticContainer | null = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (container === null && order.containers) {
                        order.containers.map((c) => {
                            if (c.id == id) {
                                container = c;
                            }
                        })
                    }
                })
            }
        }
    }

    return container;
}

const findContainerByNumber = async (number: bigint | string, orderId: bigint | string) => {
    let containerData:any = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (order.order.id == orderId && containerData === null && order.containers) {
                        order.containers.map((c) => {
                            if (c.main_number == number) {

                                if (c.status == 'DONE') {
                                    return 'CONTAINER_DONE';
                                }

                                containerData = c.id;
                            }
                        })
                    }
                })
            }
        }
    }

    return containerData ? containerData : 'NO_CONTAINER';
}

const orderDefects = async (specificationId: number|string) => {
    let defects: { specification_defects: DataSelectable[]; general_defects: DataSelectable[]; } | null = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (defects === null && order.order.specification != null && order.order.specification.id && order.order.specification.id == specificationId) {
                        defects = {
                            specification_defects: order.defects.specification_defects,
                            general_defects: posData.generalDefects,
                        };
                    }
                })
            }
        }
    }

    return defects;
}

const getWorkerData = async (orderId: string, number: string, serviceDate: string) => {
    let workerData:any = await checkWorker(orderId, number, serviceDate)
        .then(response => {
            return response;
        });

    return workerData;
}

const orderWorkers = async (orderId: number|string, serviceDate: string, page: number = 1, searchText: string = '') => {
    const perPage = 50;
    let workers:ReportWorker[] = [];
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (workers.length === 0 && order.order.id == orderId) {
                        workers = posData.workers;
                    }
                })
            }
        }
    }

    if (workers && searchText) {
        workers.filter(ele => ele.name.toLowerCase().includes(searchText.toLowerCase()));
    }

    if (workers) {
        return Object.entries(workers).slice((page - 1) * perPage, ((page - 1) * perPage) + perPage).map(worker => worker[1]);
    }

    return workers;
}

const workerExistsInReport = (report:OfflineDataReport, workerNumber:string) => {
    let workers:ReportWorker[] = report.workers;
    let workerExists = false;

    workers.map((worker: ReportWorker) => {
        if (worker.number == workerNumber) {
            workerExists = true;
        }
    })

    return workerExists;
}

const saveReport = async (order: Order, data: Report, reportId?: string, draft = false, onlineBackup = false, multipleOrders: boolean = false, logisticContainer = '0', closeContainer: boolean = false) => {
    if (!data.offlineUuid) {
        data.offlineUuid = uuidv4();
    }

    if (!reportId || reportId === '0') {
        reportId = data.offlineUuid;
    }

    data.orderId = order.id;
    data.order = order;
    data.orderNumber = order.formatted_number;
    data.status = draft ? ReportStatus.OFFLINE_DRAFT : ReportStatus.OFFLINE_READY_TO_SYNC;
    data.reportId = reportId;
    data.onlineBackup = onlineBackup;
    data.multipleOrders = multipleOrders;
    data.createdAt = getServerNow().format();
    data.logisticContainerNumber = logisticContainer;
    data.closeLogisticContainer = closeContainer;

    if (logisticContainer) {
        try {
            let containerObject = await findContainerById(logisticContainer);
            if (containerObject) {
                data.logisticContainer = containerObject;
            }
        } catch (e) {

        }
    }

    let workerId = await Preferences.get({'key': 'worker_id'}).then((value => {
        return value.value;
    }));

    if (workerId) {
        data.createdByWorkerId = workerId;
    }

    let key:string = OFFLINE_REPORT_ID_PREFIX + data.reportId;
    await offlineStorageSet(key, data);

    return reportId ? getReport(reportId) : [];
}

const getReports = async (status: ReportStatus, rawOutput = false, skipOnlineBackup = false, onlyBackup = false) => {
    let reports:any[] = [];
    let keys = await SecureStoragePlugin.keys();
    let userName = await Preferences.get({
        key: 'full_name'
    });

    let workerId = await Preferences.get({'key': 'worker_id'}).then((value => {
        return value.value;
    }));

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith(OFFLINE_REPORT_ID_PREFIX))) {
            let report:Report = await offlineStorageGet(key) as unknown as Report;
            if (workerId && report.createdByWorkerId == workerId && report.status === status && (!skipOnlineBackup || (skipOnlineBackup && !report.onlineBackup))) {
                if (onlyBackup && !report.onlineBackup) {
                    continue;
                }

                if (!rawOutput) {
                    reports.push({
                        offline: true,
                        online_backup: report.onlineBackup,
                        multiple_orders: report.additionalOrders?.length,
                        avg_time_per_component: "",
                        checked_parts_sum: 0,
                        created_by: userName.value,
                        hours_sum_humanized: "",
                        is_editable: false,
                        order_factory_number: report.orderFactoryNumber || '',
                        place_of_service: report.order?.place_of_service.name,
                        workers: [],
                        components: {
                            numbers: [],
                            names: []
                        },
                        order: {
                            id: report.orderId && parseInt(report.orderId) || 0,
                            formatted_number: report.orderFactoryNumber || '',
                            sub_type: '',
                            complaint_number: '',
                            enable_nested_reporting: false,
                            enable_single_components: false,
                            is_extended_reporting: false,
                            nested_reporting_components: [],
                            specification: {
                                id: report.specificationId && parseInt(report.specificationId) || 0
                            }
                        },
                        service_date: new Date(report.serviceDate),
                        status: report.status,
                        id: report.reportId ?? key.replace(OFFLINE_REPORT_ID_PREFIX, ''),
                        formatted_number: report.orderNumber + '/' + moment(report.createdAt, moment.ISO_8601).format('DD-MM-YYYY HH:mm:ss'),
                        ep2_scanned_specification_id: report.specificationId && parseInt(report.specificationId) || undefined,
                        created_at: report.createdAt,
                        internal_costs_type: report.internalCostType,
                    });
                } else {
                    reports.push(report);
                }
            }
        }
    }

    if (!rawOutput) {
        reports.sort((a, b) => {
            return moment(a.created_at).isBefore(moment(b.created_at)) ? 1 : -1;
        })
    }

    return reports;
}

const removeReport = async (reportId: number|string) => {
    await offlineStorageRemove(OFFLINE_REPORT_ID_PREFIX + reportId);
}

export const getMeasuringEquipments = async (specificationId: number | string, serviceDate: string) => {
    let keys = await SecureStoragePlugin.keys();

    let measurements: any = [];
    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (measurements.length === 0 && order.order.specification != null && order.order.specification.id && order.order.specification.id == specificationId && order.measuringCategories) {
                        measurements = order.measuringCategories;
                    }
                })
            }
        }
    }

    return measurements;
}

export const getMeasuringEquipmentsItems = async (specificationId: number | string, serviceDate: string, categoryId: number | string) => {
    let keys = await SecureStoragePlugin.keys();

    let measurementsItems: any = [];
    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (measurementsItems.length === 0 && order.order.specification != null && order.order.specification.id && order.order.specification.id == specificationId && order.measuringCategories) {
                        order.measuringCategories.map((category: OfflineDataReportMeasurement) => {
                            if (category.measuring_equipment_item_category.id == categoryId) {
                                measurementsItems = category.items;
                            }
                        })
                    }
                })
            }
        }
    }

    return measurementsItems;
}

const internalCosts = async (orderId: number|string) => {
    let internalCosts: DataSelectable[] | null = null;
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let posData:OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;
            if (posData.orders.length > 0) {
                posData.orders.map((order: OfflineDataOrder) => {
                    if (internalCosts === null && order.order.id == orderId) {
                        internalCosts = posData.internalCosts;
                    }
                })
            }
        }
    }

    return internalCosts;
}

const checkWorkerAvailability = async (workerId: number, date: string, startTime: string, endTime: string, placeOfServiceId: number): Promise<boolean> => {
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length === 0) {
        return true;
    }

    let shouldValidate: boolean | undefined = undefined;
    let allWorkerAvailabilities: { [date: string]: WorkerAvailabilityRange[] } = {};

    for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
        let posData: OfflineDataReport = await offlineStorageGet(key) as unknown as OfflineDataReport;

        const worker = posData.workers.find(w => Number(w.id) === workerId);

        if (worker && worker.shouldValidateAvailability !== undefined && shouldValidate === undefined) {
            shouldValidate = worker.shouldValidateAvailability;
        }

        if (posData.availabilities && posData.availabilities[workerId.toString()]) {
            allWorkerAvailabilities = {
                ...allWorkerAvailabilities,
                ...posData.availabilities[workerId.toString()],
            };
        }
    }

    if (shouldValidate === false || shouldValidate === undefined) {
        return true;
    }

    if (Object.keys(allWorkerAvailabilities).length === 0) {
        return false;
    }

    const preferredPlacesEnabled = await isAvailabilityPreferredPlacesEnabled();
    const isOvernightShift = endTime < startTime;
    if (isOvernightShift) {
        const currentDayRanges = allWorkerAvailabilities[date];
        const nextDay = moment(date).add(1, 'day').format('YYYY-MM-DD');
        const nextDayRanges = allWorkerAvailabilities[nextDay];

        let currentDayValid = false;
        let nextDayValid = false;

        if (currentDayRanges) {
            for (const range of currentDayRanges) {
                if (startTime >= range.startTime && range.endTime >= startTime) {
                    if (!preferredPlacesEnabled || range.agreementForOuterPlaces || range.preferredPlaceIds.includes(placeOfServiceId)) {
                        currentDayValid = true;
                        break;
                    }
                }
            }
        }

        if (nextDayRanges) {
            for (const range of nextDayRanges) {
                if (endTime <= range.endTime && range.startTime <= endTime) {
                    if (!preferredPlacesEnabled || range.agreementForOuterPlaces || range.preferredPlaceIds.includes(placeOfServiceId)) {
                        nextDayValid = true;
                        break;
                    }
                }
            }
        }

        return currentDayValid && nextDayValid;
    } else {
        const dayRanges = allWorkerAvailabilities[date];

        if (!dayRanges || dayRanges.length === 0) {
            return false;
        }

        for (const range of dayRanges) {
            if (startTime >= range.startTime && endTime <= range.endTime) {
                if (!preferredPlacesEnabled || range.agreementForOuterPlaces || range.preferredPlaceIds.includes(placeOfServiceId)) {
                    return true;
                }
            }
        }

        return false;
    }
};

const updateReportStatus = async (reportId: string, status: ReportStatus, errorMessage?: string) => {
    const key = OFFLINE_REPORT_ID_PREFIX + reportId;
    const report = await offlineStorageGet(key) as unknown as Report;

    if (report) {
        report.status = status;
        if (errorMessage !== undefined) {
            report.syncErrorMessage = errorMessage;
        }
        await offlineStorageSet(key, report);
    }
}

export {
    findSpecificationByNumber,
    checkSpecification,
    findOrderById,
    findOrderByNumber,
    getReport,
    orderComponentsNumbers,
    orderDefects,
    getWorkerData,
    orderWorkers,
    saveReport,
    getReports,
    removeReport,
    findContainerById,
    findContainerByNumber,
    orderContainers,
    internalCosts,
    checkWorkerAvailability,
    updateReportStatus
}